import React, { useState, useEffect, useContext, useRef } from "react";
import type { ChangeEvent } from "react";
import {
  Grid,
  Column,
  Section,
  Heading,
  RadioButtonGroup,
  RadioButton,
  TextInput,
  Button,
  Loading,
  Dropdown,
  FileUploader,
  Checkbox,
} from "@carbon/react";
import {
  postToOpenElisServer,
  postToOpenElisServerFormData,
} from "../../../utils/Utils";
import {
  useInvalidateServerData,
  useServerData,
} from "../../../utils/useServerData";
import {
  AlertDialog,
  NotificationKinds,
} from "../../../common/CustomNotification";
import {
  ConfigurationContext,
  NotificationContext,
} from "../../../layout/Layout";

import { FormattedMessage, useIntl } from "react-intl";

interface GenericConfigEditProps {
  menuType: string;
  ID: string;
  /** Called when the editor is finished with, saved or abandoned. */
  onDone: () => void;
}

interface ConfigLocalization {
  english?: string;
  french?: string;
  localeValues?: Record<string, string>;
}

interface FormEntryConfig {
  paramName: string;
  description: string;
  value: string;
  valueType: "boolean" | "dictionary" | "logoUpload" | "text" | "freeText";
  tag?: string;
  dictionaryValues?: string[];
  localization?: ConfigLocalization;
}

interface NotificationContextValue {
  notificationVisible: boolean;
  setNotificationVisible: (visible: boolean) => void;
  addNotification: (notification: {
    kind: string;
    title: string;
    message: string;
  }) => void;
}

const GenericConfigEdit = ({
  menuType,
  ID,
  onDone,
}: GenericConfigEditProps) => {
  const intl = useIntl();

  const [FormEntryConfig, setFormEntryConfig] =
    useState<FormEntryConfig | null>(null);
  const [radioValue, setRadioValue] = useState("");
  const [textInputEnglishValue, setTextInputEnglishValue] = useState("");
  const [textInputFrenchValue, setTextInputFrenchValue] = useState("");
  const [textInputValue, setTextInputValue] = useState("");
  const [selectedDictionaryValue, setSelectedDictionaryValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);

  const [img, setImg] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { notificationVisible, setNotificationVisible, addNotification } =
    useContext(NotificationContext) as NotificationContextValue;
  const configurationContext = useContext(ConfigurationContext) as {
    reloadConfiguration?: () => void;
  } | null;
  const reloadConfiguration =
    configurationContext?.reloadConfiguration ?? (() => undefined);

  const { data: storedConfig, isLoading: configLoading } =
    useServerData<FormEntryConfig>(`/rest/${menuType}?ID=${ID}`);
  const invalidateServerData = useInvalidateServerData();

  // A logo is held apart from the rest of the config, so it is a second read,
  // held off until the config says there is one.
  const { data: storedLogo } = useServerData<{ value: string }>(
    storedConfig?.valueType === "logoUpload"
      ? `/dbImage/siteInformation/${storedConfig.paramName}`
      : null,
  );

  const seededRecord = useRef<string | null>(null);
  const recordKey = storedConfig
    ? `${menuType}:${ID}:${storedConfig.paramName}`
    : null;

  const seedFrom = (res?: FormEntryConfig) => {
    if (!res) return;
    setFormEntryConfig(res);
    setTextInputEnglishValue(
      res.localization?.english || res.localization?.localeValues?.en || "",
    );
    setTextInputFrenchValue(
      res.localization?.french || res.localization?.localeValues?.fr || "",
    );
    setRadioValue(res.valueType === "boolean" ? res.value : "");
    setSelectedDictionaryValue(res.valueType === "dictionary" ? res.value : "");
    setTextInputValue(res.value);
    setImg(null);
    setFile(null);
    setRemoveImage(false);
  };

  useEffect(() => {
    if (!storedConfig || !recordKey || seededRecord.current === recordKey) {
      return;
    }
    seededRecord.current = recordKey;
    seedFrom(storedConfig);
  }, [storedConfig, recordKey]);

  // The image is fetched separately and can arrive after the user has chosen
  // a file or asked to remove it. It may update the server preview, never a
  // draft image decision.
  useEffect(() => {
    if (storedConfig?.valueType !== "logoUpload" || file || removeImage) {
      return;
    }
    setImg(storedLogo?.value ?? null);
  }, [storedConfig?.valueType, storedLogo, file, removeImage]);

  const updateFormEntryConfig = (newState: Partial<FormEntryConfig>) => {
    setFormEntryConfig((prevState) => ({
      ...prevState!,
      ...newState,
    }));
  };

  const handleRadioChange = (value: string) => {
    setRadioValue(value);
    updateFormEntryConfig({ value });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setTextInputValue(newValue);
    updateFormEntryConfig({ value: newValue });
  };

  const handleInputEnglishChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setTextInputEnglishValue(newValue);
    updateFormEntryConfig({
      localization: {
        ...FormEntryConfig!.localization,
        english: newValue,
      },
    });
  };

  const handleInputFrenchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setTextInputFrenchValue(newValue);
    updateFormEntryConfig({
      localization: {
        ...FormEntryConfig!.localization,
        french: newValue,
      },
    });
  };

  const handleDictionaryChange = (event: { selectedItem: string }) => {
    const newValue = event.selectedItem;
    setSelectedDictionaryValue(newValue);
    updateFormEntryConfig({ value: newValue });
  };

  const showAlertMessage = (msg: string, kind: string) => {
    setNotificationVisible(true);
    addNotification({
      kind: kind,
      title: intl.formatMessage({ id: "notification.title" }),
      message: msg,
    });
  };

  const handleSubmitButton = () => {
    setIsLoading(true);
    if (FormEntryConfig!.valueType === "logoUpload") {
      const formData = new FormData();
      if (!removeImage) {
        formData.append("logoFile", file as File);
      }
      formData.append("logoName", FormEntryConfig!.paramName);
      formData.append("removeImage", removeImage ? "true" : "false");

      postToOpenElisServerFormData(`/rest/logoUpload`, formData, handleSubmit);
    } else {
      const body = JSON.stringify(FormEntryConfig);
      postToOpenElisServer(`/rest/${menuType}?ID=${ID}`, body, handleSubmit);
    }
  };

  const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    const file = files![0];
    if (!file) {
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      showAlertMessage(
        intl.formatMessage({ id: "admin.config.logo.error.fileTooLarge" }),
        NotificationKinds.error,
      );
      event.target.value = "";
      return;
    }
    setFile(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result;
      setImg(base64String as string);
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = (status: number) => {
    setIsLoading(false);
    if (status === 200) {
      showAlertMessage(
        intl.formatMessage({ id: "save.config.success.msg" }),
        NotificationKinds.success,
      );
      invalidateServerData();
      reloadConfiguration();
      onDone();
    } else {
      showAlertMessage(
        intl.formatMessage({ id: "server.error.msg" }),
        NotificationKinds.error,
      );
    }
  };

  return (
    <div className="adminPageContent">
      {(isLoading || configLoading) && (
        <Loading
          description={intl.formatMessage({ id: "loading.description" })}
        />
      )}
      {notificationVisible === true ? <AlertDialog /> : ""}
      {FormEntryConfig && (
        <>
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <Section>
                <Heading>
                  <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.editRecord" />
                </Heading>
              </Section>
              <br />
            </Column>
          </Grid>
          <div className="orderLegendBody">
            <div className="gridBoundary">
              <Grid fullWidth={true}>
                <Column lg={3} md={2} sm={1}>
                  <h4>
                    <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.name" />
                  </h4>
                </Column>
                <Column lg={3} md={6} sm={3}>
                  {FormEntryConfig.paramName}
                </Column>
              </Grid>
              <br />
              <Grid fullWidth={true}>
                <Column lg={3} md={2} sm={2}>
                  <h4>
                    <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.description" />
                  </h4>
                </Column>
                <Column lg={7} md={6} sm={4}>
                  {FormEntryConfig.description}
                </Column>
              </Grid>
              <br />
              {FormEntryConfig.valueType === "boolean" && (
                <Grid fullWidth={true}>
                  <Column lg={3} md={2} sm={1}>
                    <h4>
                      <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.value" />
                    </h4>
                  </Column>
                  <Column lg={5} md={6} sm={3}>
                    <RadioButtonGroup
                      name="radioValue"
                      valueSelected={radioValue}
                      onChange={handleRadioChange}
                    >
                      <RadioButton
                        labelText={intl.formatMessage({ id: "true.label" })}
                        value="true"
                        id="radio-1"
                      />
                      <RadioButton
                        labelText={intl.formatMessage({ id: "false.label" })}
                        value="false"
                        id="radio-2"
                      />
                    </RadioButtonGroup>
                  </Column>
                </Grid>
              )}
              {FormEntryConfig.valueType === "dictionary" && (
                <>
                  <Grid fullWidth={true}>
                    <Column lg={3} md={2} sm={1}>
                      <h4>
                        <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.value" />
                      </h4>
                    </Column>
                    <Column lg={3} md={6} sm={3}>
                      <Dropdown
                        id="dictionaryDropdown"
                        items={FormEntryConfig.dictionaryValues}
                        selectedItem={selectedDictionaryValue}
                        onChange={handleDictionaryChange}
                      />
                    </Column>
                  </Grid>
                </>
              )}
              {FormEntryConfig.valueType === "logoUpload" && (
                <>
                  <Grid fullWidth={true}>
                    <Column lg={3}>
                      <h4>
                        <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.value" />
                      </h4>
                    </Column>
                    <Column lg={3} md={6} sm={3}>
                      {!removeImage && (
                        <>
                          <FileUploader
                            buttonLabel="Choose file"
                            buttonKind="primary"
                            size="sm"
                            filenameStatus="edit"
                            accept={[".jpg", ".png", ".gif"]}
                            multiple={false}
                            disabled={false}
                            iconDescription="Delete file"
                            onChange={handleFileUpload}
                          />
                          <p
                            style={{
                              fontSize: "0.75rem",
                              color: "#525252",
                              marginTop: "0.25rem",
                            }}
                          >
                            <FormattedMessage id="admin.config.logo.formats" />
                          </p>
                        </>
                      )}
                    </Column>
                  </Grid>
                  <br />
                  {img ? (
                    <Grid>
                      <Column lg={3}>
                        <img
                          src={img}
                          alt="Logo"
                          style={{ maxWidth: "100px" }}
                        />
                      </Column>
                    </Grid>
                  ) : null}

                  <br />
                  <Grid>
                    <Column lg={3}>
                      <Checkbox
                        labelText={`Remove Image`}
                        id="checkbox-label-1"
                        checked={removeImage}
                        onChange={() => {
                          setRemoveImage(!removeImage);
                          setImg(null);
                        }}
                      />
                    </Column>
                  </Grid>
                </>
              )}
              {(FormEntryConfig.valueType === "text" ||
                FormEntryConfig.valueType === "freeText") && (
                <>
                  <Grid fullWidth={true}>
                    <Column lg={3}>
                      <h4>
                        <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.value" />
                      </h4>
                    </Column>
                    {FormEntryConfig.tag !== "localization" && (
                      <Column lg={8} sm={3}>
                        <TextInput
                          id="textInput"
                          value={textInputValue}
                          onChange={handleInputChange}
                        />
                      </Column>
                    )}
                  </Grid>
                  {FormEntryConfig.tag === "localization" && (
                    <>
                      <br />
                      <Grid>
                        <Column lg={8} sm={2} md={4}>
                          <TextInput
                            id="myInputEnglish"
                            labelText={<FormattedMessage id="english.label" />}
                            value={textInputEnglishValue}
                            onChange={handleInputEnglishChange}
                          />
                        </Column>
                        <Column lg={8} sm={2} md={4}>
                          <TextInput
                            id="myInputFrench"
                            labelText={<FormattedMessage id="french.label" />}
                            value={textInputFrenchValue}
                            onChange={handleInputFrenchChange}
                          />
                        </Column>
                      </Grid>
                    </>
                  )}
                </>
              )}
              <br />
              <br />
              <Grid fullWidth={true}>
                <Column lg={2} sm={2}>
                  <Button
                    data-cy="save-Button"
                    onClick={handleSubmitButton}
                    disabled={isLoading}
                  >
                    <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.button.save" />
                  </Button>
                </Column>
                <Column lg={2}>
                  <Button data-cy="exit-Button" onClick={onDone}>
                    <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.button.exit" />
                  </Button>
                </Column>
              </Grid>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GenericConfigEdit;
